import { motion } from 'framer-motion';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import AnimatedButton from '../../components/ui/AnimatedButton';
import { StaggerList, StaggerItem } from '../../components/ui/StaggerList';
import MindoraCharacter from '../../components/character/MindoraCharacter';
import SpeechBubble from '../../components/ui/SpeechBubble';
import CheckmarkDraw from '../../components/effects/CheckmarkDraw';

const trustPoints = [
  { emoji: '🔒', text: 'No ads. Ever.' },
  { emoji: '🚫', text: 'No social features.' },
  { emoji: '👨‍👩‍👧', text: 'You control everything.' },
  { emoji: '🛡️', text: 'COPPA compliant & kid-safe.' },
];

interface Props {
  onNext: () => void;
}

export default function WelcomeTrustScreen({ onNext }: Props) {
  const reduced = useReducedMotion();

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12">
      {/* Large heading */}
      <motion.h2
        className="font-display text-kid-3xl text-kid-text text-center mb-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3 } : springs.gentle}
      >
        Built for kids' safety.
      </motion.h2>
      <motion.p
        className="text-kid-text-soft font-kid text-kid-base text-center mb-8 max-w-sm"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3, delay: 0.1 } : { ...springs.gentle, delay: 0.15 }}
      >
        No ads. No social. You control everything.
      </motion.p>

      {/* Trust cards with checkmarks */}
      <StaggerList stagger="slow" className="flex flex-col gap-3 max-w-sm w-full mb-6">
        {trustPoints.map((point, i) => (
          <StaggerItem key={i}>
            <div className="flex items-center gap-4 bg-kid-surface rounded-kid p-4 shadow-kid border-2 border-kid-border">
              <CheckmarkDraw trigger size={24} color="var(--kid-meadow)" />
              <span className="text-xl" role="img" aria-hidden="true">
                {point.emoji}
              </span>
              <span className="text-kid-text font-kid font-semibold">{point.text}</span>
            </div>
          </StaggerItem>
        ))}
      </StaggerList>

      {/* Character + speech bubble */}
      <motion.div
        className="flex flex-col items-center mb-8"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={reduced ? { duration: 0.3, delay: 0.5 } : { ...springs.gentle, delay: 0.6 }}
      >
        <MindoraCharacter mood="idle" size="lg" />
        <SpeechBubble className="mt-4 max-w-xs">
          <p className="text-kid-text-soft font-kid text-kid-sm text-center">
            Your family's privacy comes first. Always.
          </p>
        </SpeechBubble>
      </motion.div>

      {/* CTA — meadow green */}
      <AnimatedButton
        variant="marshmallow"
        className="btn-marshmallow-meadow"
        onClick={onNext}
      >
        Create your free account
      </AnimatedButton>
    </div>
  );
}
